import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // First try token from localStorage
      const token = localStorage.getItem('pricelens_token');
      if (token) {
        const { data } = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setUser(data);
      } else {
        // Try cookie-based auth
        const { data } = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(data);
      }
    } catch (error) {
      setUser(false);
      localStorage.removeItem('pricelens_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    // Store token in localStorage as fallback
    if (data.token) {
      localStorage.setItem('pricelens_token', data.token);
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore errors on logout
    }
    localStorage.removeItem('pricelens_token');
    setUser(false);
  };

  const isAdmin = user?.role === 'admin';
  const isAuthenticated = !!user && user !== false;

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      isAdmin, 
      isAuthenticated,
      checkAuth 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
